import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

const UIDataGridTable = ({
  columns,
  rows,
  minWidth,
  height = 560,
  rowHeight = 68,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  sx,
  density = "standard",
  hideFooter = false,
  ...props
}) => {
  const theme = useTheme();
  const isCompact = density === "compact";

  const resolvedMinWidth = useMemo(() => {
    if (minWidth) return minWidth;

    return columns.reduce((total, column) => {
      const baseWidth = column.width ?? column.minWidth ?? 160;
      return total + baseWidth;
    }, 0);
  }, [columns, minWidth]);

  const widthOverrideSx = useMemo(() => {
    return columns.reduce((styles, column) => {
      const width = column.width ?? column.minWidth ?? column.maxWidth;

      if (!width || !column.field) {
        return styles;
      }

      styles[`& .MuiDataGrid-columnHeader[data-field="${column.field}"]`] = {
        width: `${width}px !important`,
        minWidth: `${width}px !important`,
        maxWidth: `${width}px !important`,
      };
      styles[`& .MuiDataGrid-cell[data-field="${column.field}"]`] = {
        width: `${width}px !important`,
        minWidth: `${width}px !important`,
        maxWidth: `${width}px !important`,
      };

      return styles;
    }, {});
  }, [columns]);

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        backgroundColor: "background.paper",
      }}
    >
      <Box sx={{ minWidth: resolvedMinWidth }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          rowHeight={rowHeight}
          pageSizeOptions={pageSizeOptions}
          density={density}
          hideFooter={hideFooter}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: pageSizeOptions[0] ?? 10,
                page: 0,
              },
            },
          }}
          sx={{
            "&&": {
              border: 0,
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              minWidth: resolvedMinWidth,
            },
            border: 0,
            bgcolor: "background.paper",
            color: "text.primary",
            fontSize: isCompact ? 13 : 14,
            "&& .MuiDataGrid-main": {
              minWidth: resolvedMinWidth,
            },
            "&& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha(theme.palette.secondary.main, 0.03),
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: `${theme.palette.text.primary} !important`,
            },
            "&& .MuiDataGrid-columnHeader": {
              px: isCompact ? 1.5 : 2,
              py: isCompact ? 1 : 1.5,
              backgroundColor: alpha(theme.palette.secondary.main, 0.03),
              minHeight: `${isCompact ? 46 : 56}px !important`,
              maxHeight: `${isCompact ? 46 : 56}px !important`,
              color: `${theme.palette.text.primary} !important`,
              width: "unset !important",
              maxWidth: "unset !important",
            },
            "&& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isCompact ? "12px !important" : "13px !important",
              fontWeight: 700,
              color: `${theme.palette.text.primary} !important`,
              whiteSpace: "nowrap",
              lineHeight: 1.2,
              letterSpacing: isCompact ? "0.01em" : 0,
            },
            "&& .MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "flex-start !important",
            },
            "&& .MuiDataGrid-columnHeaderTitleContainerContent": {
              justifyContent: "flex-start !important",
              width: "100%",
            },
            "&& .MuiDataGrid-row": {
              minHeight: `${rowHeight}px !important`,
              maxHeight: `${rowHeight}px !important`,
              backgroundColor: "#FFFFFF !important",
              color: `${theme.palette.text.primary} !important`,
              width: `${resolvedMinWidth}px !important`,
            },
            "&& .MuiDataGrid-row:hover": {
              backgroundColor: `${alpha(theme.palette.primary.main, 0.028)} !important`,
            },
            "&& .MuiDataGrid-cell": {
              px: isCompact ? 1.5 : 2,
              py: isCompact ? 0.75 : 1.5,
              borderColor: theme.palette.divider,
              fontSize: isCompact ? 13 : 14,
              lineHeight: isCompact ? 1.25 : 1.35,
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              alignItems: "center",
              color: `${theme.palette.text.primary} !important`,
              backgroundColor: "transparent !important",
              minWidth: "unset !important",
              maxWidth: "unset !important",
            },
            "&& .MuiDataGrid-cellContent": {
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: isCompact ? 13 : 14,
              color: `${theme.palette.text.primary} !important`,
              opacity: "1 !important",
            },
            "&& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${theme.palette.divider}`,
              minHeight: isCompact ? 48 : 56,
              backgroundColor: alpha(theme.palette.secondary.main, 0.015),
            },
            "&& .MuiTablePagination-root, && .MuiTablePagination-selectLabel, && .MuiTablePagination-displayedRows":
              {
                fontSize: isCompact ? 12 : 13,
                color: theme.palette.text.secondary,
              },
            "&& .MuiTablePagination-toolbar": {
              minHeight: isCompact ? 48 : 56,
            },
            "&& .MuiDataGrid-virtualScroller": {
              overflowX: "hidden !important",
            },
            "&& .MuiDataGrid-virtualScrollerContent": {
              width: `${resolvedMinWidth}px !important`,
              minWidth: `${resolvedMinWidth}px !important`,
            },
            "&& .MuiDataGrid-virtualScrollerRenderZone": {
              width: `${resolvedMinWidth}px !important`,
              minWidth: `${resolvedMinWidth}px !important`,
            },
            "&& .MuiDataGrid-cell:focus, && .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "&& .MuiDataGrid-columnHeader:focus, && .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "&& .MuiDataGrid-menuIcon": {
              display: "none",
            },
            "&& .MuiDataGrid-iconSeparator": {
              display: "none",
            },
            "&& .MuiDataGrid-columnSeparator": {
              color: theme.palette.divider,
            },
            "&& .MuiDataGrid-overlay": {
              minHeight: 160,
            },
            "&& .MuiDataGrid-scrollbar--horizontal": {
              display: "none",
            },
            "&& .MuiDataGrid-columnHeadersInner, && .MuiDataGrid-columnHeaders .MuiDataGrid-row":
              {
                width: `${resolvedMinWidth}px !important`,
                minWidth: `${resolvedMinWidth}px !important`,
              },
            "&& .MuiDataGrid-cell[data-field='actions']": {
              justifyContent: "center",
            },
            "&& .MuiDataGrid-columnHeader[data-field='actions'] .MuiDataGrid-columnHeaderTitleContainer":
              {
                justifyContent: "center !important",
              },
            "&& .MuiCheckbox-root": {
              p: isCompact ? 0.5 : 0.75,
            },
            ...widthOverrideSx,
            ...sx,
          }}
          {...props}
        />
      </Box>
    </Box>
  );
};

export default UIDataGridTable;
